import { useState } from "preact/hooks";
import { useResource } from "@tenergy/web-core/dashboard";
import { adminReads, adminWrites } from "../api/admin-api.js";
import type { AdminAccount, AdminRole } from "../api/types.js";
import type { Router } from "../router.js";
import { MutationForm, fieldError } from "../forms/mutation-form.js";
import { count, sunAmount, trx, utc } from "../format.js";
import { useRememberedChoice } from "../lib/remembered-choice.js";
import {
  Address,
  Blank,
  Button,
  EmptyState,
  ErrorNote,
  Field,
  Note,
  Panel,
  Pill,
  ScreenHeader,
  ShortId,
  SignInMethods,
  Spinner,
  Table,
  Td,
  Th,
  inputClass,
} from "../ui.js";

const ACCOUNT_STATUSES = ["", "unfunded", "active", "suspended", "closed"] as const;
type AccountStatusFilter = (typeof ACCOUNT_STATUSES)[number];

/**
 * Customer accounts, their balances, their ledger (`GET /v1/admin/accounts/{id}/ledger`,
 * viewer), and the one movement an operator can make by hand.
 *
 * A LEDGER ADJUSTMENT IS DOUBLE-ENTRY AND AUDITED, like every other posting: the customer's
 * balance against platform revenue, so the books still balance, with a reason written in the
 * same transaction. It is `editor` only, and the form is not rendered at all for a viewer.
 */
export function AccountsScreen({ role, router }: { role: AdminRole; router: Router }) {
  const [address, setAddress] = useState("");
  const [accountId, setAccountId] = useState("");
  // Default "active" (owner 2026-10-11); the last choice is remembered per browser. "" = every status.
  const [status, setStatus] = useRememberedChoice("accounts.status", ACCOUNT_STATUSES, "active");
  const [adjusting, setAdjusting] = useState<AdminAccount | null>(null);
  const [ledgerFor, setLedgerFor] = useState<AdminAccount | null>(null);
  const [depositFor, setDepositFor] = useState<AdminAccount | null>(null);

  const accounts = useResource(
    (signal) => {
      void signal;
      return adminReads.accounts({
        ...(address === "" ? {} : { address }),
        ...(accountId === "" ? {} : { accountId }),
        ...(status === "" ? {} : { status }),
        limit: 100,
      });
    },
    [address, accountId, status],
  );

  return (
    <>
      <ScreenHeader
        title="Accounts"
        lead="Search by owner address, by account id prefix or by status. Balances are the ledger's, read per account. Profile opens one customer on one screen."
      />

      <Panel>
        <div class="grid gap-2 sm:grid-cols-3">
          <input
            class={inputClass}
            aria-label="Owner address"
            placeholder="owner address (exact)"
            value={address}
            onInput={(event) => setAddress((event.currentTarget as HTMLInputElement).value.trim())}
          />
          <input
            class={inputClass}
            aria-label="Account id"
            placeholder="account id (prefix)"
            value={accountId}
            onInput={(event) => setAccountId((event.currentTarget as HTMLInputElement).value.trim())}
          />
          <select
            class={inputClass}
            aria-label="Status"
            value={status}
            onChange={(event) => setStatus((event.currentTarget as HTMLSelectElement).value as AccountStatusFilter)}
          >
            <option value="">every status</option>
            {ACCOUNT_STATUSES.filter((value) => value !== "").map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </div>
      </Panel>

      {adjusting === null ? null : (
        <div class="mt-3">
          <Panel>
            <LedgerAdjustmentForm
              account={adjusting}
              onDone={() => {
                setAdjusting(null);
                accounts.reload();
              }}
              onCancel={() => setAdjusting(null)}
            />
          </Panel>
        </div>
      )}

      <div class="mt-3">
        <Panel title="Accounts">
          {accounts.loading ? (
            <Spinner />
          ) : accounts.error !== null ? (
            <ErrorNote error={accounts.error} onRetry={() => accounts.reload()} />
          ) : (
            <Table
              rows={(accounts.data?.data ?? []).length}
              empty="No account matches these filters. Clear the address or id to list the newest accounts."
              head={
                <tr>
                  <Th>Account</Th>
                  <Th>Sign-in</Th>
                  <Th>Status</Th>
                  <Th numeric>Balance</Th>
                  <Th numeric>Reserved</Th>
                  <Th>Created</Th>
                  <Th>{""}</Th>
                </tr>
              }
            >
              {(accounts.data?.data ?? []).map((account) => (
                <tr key={account.id}>
                  <Td mono>
                    <ShortId value={account.id} />
                  </Td>
                  <Td mono>
                    {/* The owners' ways in (owner 2026-10-17); one account serves mainnet and Nile, so no network column. */}
                    {(account.sign_in ?? []).length > 0 ? (
                      <SignInMethods methods={account.sign_in!} />
                    ) : account.owner_address === null ? (
                      <Blank title="Created by a direct transfer; no member signs in" />
                    ) : (
                      <Address value={account.owner_address} />
                    )}
                  </Td>
                  <Td>
                    <Pill value={account.status} />
                  </Td>
                  <Td numeric>{trx(account.balance_sun)}</Td>
                  <Td numeric>{trx(account.reserved_sun)}</Td>
                  <Td mono>{utc(account.created_at)}</Td>
                  <Td>
                    <span class="inline-flex gap-1.5">
                      <a
                        href={`/accounts/${encodeURIComponent(account.id)}`}
                        onClick={(event) => router.onNavigate(event as MouseEvent, "accounts", account.id)}
                        class="inline-flex min-h-8 items-center rounded-[12px] border border-line bg-surface px-3 text-[12px] font-medium text-ink hover:border-accent/60"
                      >
                        Profile
                      </a>
                      <Button tone="ghost" onClick={() => setLedgerFor(account)}>
                        Ledger
                      </Button>
                      <Button tone="ghost" onClick={() => setDepositFor(account)}>
                        Deposit address
                      </Button>
                      {role === "editor" ? (
                        <Button tone="ghost" onClick={() => setAdjusting(account)}>
                          Adjust ledger
                        </Button>
                      ) : null}
                    </span>
                  </Td>
                </tr>
              ))}
            </Table>
          )}
        </Panel>
      </div>

      {depositFor === null ? null : (
        <div class="mt-3">
          <DepositAddressPanel account={depositFor} role={role} onClose={() => setDepositFor(null)} />
        </div>
      )}

      <div class="mt-3">
        {ledgerFor === null ? (
          <Panel title="Ledger" subtitle="The double-entry journal behind those balances.">
            <EmptyState>Press “Ledger” on an account above to see every movement of its balance.</EmptyState>
          </Panel>
        ) : (
          <LedgerPanel account={ledgerFor} onClose={() => setLedgerFor(null)} />
        )}
      </div>
    </>
  );
}

export function LedgerAdjustmentForm({
  account,
  onDone,
  onCancel,
}: {
  account: AdminAccount;
  onDone(): void;
  onCancel(): void;
}) {
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState<"TRX" | "USDT">("TRX");
  const [referenceId, setReferenceId] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const parsed = Number(amount);
  const valid = amount !== "" && Number.isInteger(parsed) && parsed !== 0;
  const after = valid ? account.balance_sun + parsed : account.balance_sun;

  return (
    <MutationForm
      title={`Adjust the ledger for ${account.id}`}
      description="A manual double-entry movement: this account's balance against platform revenue. Positive credits the customer, negative debits them."
      changes={[
        {
          label: "balance_sun",
          before: `${account.balance_sun} (${trx(account.balance_sun)})`,
          after: valid ? `${after} (${trx(after)})` : "—",
        },
        {
          label: "movement",
          before: null,
          after: valid
            ? `${parsed > 0 ? "credit" : "debit"} ${Math.abs(parsed)} ${currency}`
            : "—",
        },
      ]}
      effect="At once, in one transaction with its audit row. There is no reversal control — a mistake is corrected by posting the opposite movement."
      submitLabel="Post adjustment"
      destructive
      busy={busy}
      disabled={!valid}
      error={error}
      reason={reason}
      onReason={setReason}
      onCancel={onCancel}
      onSubmit={() => {
        setBusy(true);
        setError(null);
        void adminWrites
          .adjustLedger({
            account_id: account.id,
            amount_sun: parsed,
            currency,
            ...(referenceId === "" ? {} : { reference_id: referenceId }),
            reason,
          })
          .then(onDone)
          .catch(setError)
          .finally(() => setBusy(false));
      }}
    >
      <Field
        label="Amount (SUN, signed)"
        hint="A whole number, not zero. Positive credits this account."
        error={fieldError(error, "amount_sun")}
      >
        <input
          class={inputClass}
          name="amount_sun"
          type="number"
          step={1}
          value={amount}
          onInput={(event) => setAmount((event.currentTarget as HTMLInputElement).value)}
        />
      </Field>
      <Field label="Currency" error={fieldError(error, "currency")}>
        <select
          class={inputClass}
          name="currency"
          value={currency}
          onChange={(event) =>
            setCurrency((event.currentTarget as HTMLSelectElement).value as "TRX" | "USDT")
          }
        >
          <option value="TRX">TRX</option>
          <option value="USDT">USDT</option>
        </select>
      </Field>
      <Field
        label="Reference (optional)"
        hint="A ticket or incident id, so the journal can be traced back to why."
        error={fieldError(error, "reference_id")}
      >
        <input
          class={inputClass}
          name="reference_id"
          value={referenceId}
          onInput={(event) => setReferenceId((event.currentTarget as HTMLInputElement).value.trim())}
        />
      </Field>
    </MutationForm>
  );
}

/**
 * One account's statement: every journal that moved its balance, newest first, with the signed
 * net per currency and the memo (an adjustment's reason). "Older" follows the API's cursor.
 */
export function LedgerPanel({ account, onClose }: { account: AdminAccount; onClose(): void }) {
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const ledger = useResource(
    (signal) => {
      void signal;
      return adminReads.accountLedger(account.id, { limit: 50, ...(cursor === undefined ? {} : { cursor }) });
    },
    [account.id, cursor],
  );
  const view = ledger.data;
  return (
    <Panel
      title={`Ledger — ${account.id}`}
      subtitle={
        view == null
          ? "The double-entry journal behind this balance."
          : `Balances: ${view.balances.length === 0 ? "no movement yet" : view.balances.map((row) => (row.currency === "TRX" ? trx(row.balance) : `${sunAmount(row.balance)} (USDT units)`)).join(" · ")}`
      }
      action={
        <span class="inline-flex gap-1.5">
          {cursor === undefined ? null : (
            <Button tone="ghost" onClick={() => setCursor(undefined)}>
              Newest
            </Button>
          )}
          {view?.next_cursor == null ? null : (
            <Button tone="ghost" onClick={() => setCursor(view.next_cursor ?? undefined)}>
              Older
            </Button>
          )}
          <Button tone="ghost" onClick={onClose}>
            Close ledger
          </Button>
        </span>
      }
    >
      {ledger.loading && view == null ? (
        <Spinner />
      ) : ledger.error !== null ? (
        <ErrorNote error={ledger.error} onRetry={() => ledger.reload()} />
      ) : (
        <Table
          rows={view?.data.length ?? 0}
          empty="This account's balance has never moved. A deposit or an order charge would appear here."
          head={
            <tr>
              <Th>At</Th>
              <Th>Kind</Th>
              <Th>Reference</Th>
              <Th>Memo</Th>
              <Th numeric>TRX</Th>
              <Th numeric>USDT units</Th>
            </tr>
          }
        >
          {(view?.data ?? []).map((line) => (
            <tr key={line.journal_id}>
              <Td mono>{utc(line.created_at)}</Td>
              <Td>
                <Pill value={line.kind} tone="neutral" />
              </Td>
              <Td mono>
                {line.reference_id === null ? (
                  <Blank />
                ) : (
                  <span title={line.reference_id}>
                    {line.reference_type ?? "ref"}: {line.reference_id.length > 20 ? `${line.reference_id.slice(0, 10)}…${line.reference_id.slice(-6)}` : line.reference_id}
                  </span>
                )}
              </Td>
              <Td class="max-w-[320px] text-[11.5px] text-ink-muted">{line.memo ?? <Blank />}</Td>
              <Td numeric class={line.amount_sun > 0 ? "text-save" : line.amount_sun < 0 ? "text-ink" : "text-ink-muted"}>
                {line.amount_sun === 0 ? <Blank /> : `${line.amount_sun > 0 ? "+" : ""}${trx(line.amount_sun)}`}
              </Td>
              <Td numeric>{line.amount_usdt === 0 ? <Blank /> : `${line.amount_usdt > 0 ? "+" : ""}${count(line.amount_usdt)}`}</Td>
            </tr>
          ))}
        </Table>
      )}
    </Panel>
  );
}

/**
 * One account's deposit address (docs/deposit-addresses.md): the address in use, every address it
 * had before, and — for an EDITOR only — "Rotate address" with a mandatory reason and the
 * allowance left under the limits (1 per 7 days, 3 per 90). A viewer sees the same history and
 * no control. A retired address still credits until `credited_until`.
 */
export function DepositAddressPanel({ account, role, onClose }: { account: AdminAccount; role: AdminRole; onClose(): void }) {
  const view = useResource(
    (signal) => {
      void signal;
      return adminReads.depositAddresses(account.id);
    },
    [account.id],
  );
  const [rotating, setRotating] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const data = view.data;
  const allowance = data?.allowance;
  const blockedUntil = allowance?.next_allowed_at ?? null;

  return (
    <Panel
      title={`Deposit address — ${account.id}`}
      subtitle={
        allowance === undefined
          ? "The account's own address, derived from its brand's seed."
          : `Rotations: ${allowance.used_in_window} of ${allowance.limits.max_per_window} used in ${allowance.limits.window_days} days, at most 1 per ${allowance.limits.min_interval_days} days${blockedUntil === null ? " — a rotation is allowed now" : ` — next allowed ${utc(blockedUntil)}`}`
      }
      action={
        <span class="inline-flex gap-1.5">
          {role === "editor" && data?.configured && !rotating ? (
            <Button tone="ghost" disabled={blockedUntil !== null} onClick={() => setRotating(true)}>
              Rotate address
            </Button>
          ) : null}
          <Button tone="ghost" onClick={onClose}>
            Close
          </Button>
        </span>
      }
    >
      {view.loading && data == null ? (
        <Spinner />
      ) : view.error !== null ? (
        <ErrorNote error={view.error} onRetry={() => view.reload()} />
      ) : data == null ? null : (
        <>
          {!data.configured ? (
            <Note>This brand has no deposit xpub configured (DEPOSIT_XPUB_&lt;BRAND&gt;); no address can be issued.</Note>
          ) : null}
          {rotating && data.active !== null ? (
            <div class="mb-3">
              <MutationForm
                title="Rotate the deposit address"
                description="Retires the address in use and issues the next one under the same account index. The customer is shown the new address; the old one still credits for 30 days, then deposits to it alert instead."
                changes={[
                  { label: "address", before: data.active.address, after: `rotation ${data.active.rotation + 1} (derived on submit)` },
                  {
                    label: "allowance",
                    before: `${allowance!.remaining_in_window} left in ${allowance!.limits.window_days} days`,
                    after: `${Math.max(0, allowance!.remaining_in_window - 1)} left; next in ${allowance!.limits.min_interval_days} days`,
                  },
                ]}
                effect="At once, with its audit row. Cannot be undone; a second rotation waits for the limit."
                submitLabel="Rotate address"
                destructive
                busy={busy}
                error={error}
                reason={reason}
                onReason={setReason}
                onCancel={() => setRotating(false)}
                onSubmit={() => {
                  setBusy(true);
                  setError(null);
                  void adminWrites
                    .rotateDepositAddress(account.id, { reason })
                    .then(() => {
                      setRotating(false);
                      setReason("");
                      view.reload();
                    })
                    .catch(setError)
                    .finally(() => setBusy(false));
                }}
              >
                {null}
              </MutationForm>
            </div>
          ) : null}
          <Table
            rows={data.history.length}
            empty="No address issued yet. The first read by the customer (or a rotation here) issues one."
            head={
              <tr>
                <Th>Address</Th>
                <Th>Status</Th>
                <Th>Path</Th>
                <Th>Issued</Th>
                <Th>Retired</Th>
                <Th>By / reason</Th>
                <Th>Credits until</Th>
              </tr>
            }
          >
            {data.history.map((row) => (
              <tr key={row.address}>
                <Td mono>
                  <Address value={row.address} />
                </Td>
                <Td>
                  <Pill value={row.status} tone={row.status === "active" ? "good" : "neutral"} />
                </Td>
                <Td mono>{row.derivation_path}</Td>
                <Td mono>{utc(row.created_at)}</Td>
                <Td mono>{row.retired_at === null ? <Blank /> : utc(row.retired_at)}</Td>
                <Td class="max-w-[280px] text-[11.5px] text-ink-muted">
                  {row.retired_by === null ? <Blank /> : `${row.retired_by.slice(0, 6)}… — ${row.reason ?? ""}`}
                </Td>
                <Td mono>{row.credited_until === null ? <Blank /> : utc(row.credited_until)}</Td>
              </tr>
            ))}
          </Table>
        </>
      )}
    </Panel>
  );
}
